import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { Sparkles, X } from "lucide-react";
import { paths, type QbLearn, type QbSyncProfile } from "@clevis/shared-types";
import { Button } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";

// "Learn from my QuickBooks": Clevis reads the company's own recent invoices
// and proposes how Clevis invoices should land in their file (items, class,
// tax, terms, where the PO goes) — each choice with its reason. Nothing
// changes until an admin applies it. Shared by Online and Desktop.

type Target = "online" | "desktop";
const PO_LABEL: Record<QbSyncProfile["poPlacement"], string> = {
  none: "not sent",
  memo: "in the memo",
  poNumber: "in QuickBooks' PO No. field",
  customField: "in the custom field",
};

function describe(p: QbSyncProfile): string[] {
  return [
    `Default item: ${p.defaultItemName ?? "“Clevis Services” (created if missing)"}`,
    p.itemRules.length > 0 ? `${p.itemRules.length} keyword rule${p.itemRules.length > 1 ? "s" : ""}` : "",
    p.className ? `Class: ${p.className}` : "",
    p.taxCodeName ? `Tax code: ${p.taxCodeName}` : "",
    p.termsName ? `Terms: ${p.termsName}` : "",
    p.poPlacement !== "none"
      ? `Builder PO ${PO_LABEL[p.poPlacement]}${p.poPlacement === "customField" && p.poCustomFieldName ? ` “${p.poCustomFieldName}”` : ""}`
      : "",
  ].filter(Boolean);
}

export function QbLearnCard({ target }: { target: Target }) {
  const { tenantId } = useAuth();
  const slug = target === "online" ? "quickbooks-online" : "quickbooks-desktop";
  const [learn, setLearn] = useState<QbLearn | null>(null);
  const [active, setActive] = useState<QbSyncProfile | null>(null);
  const [draft, setDraft] = useState<QbSyncProfile | null>(null);
  const [busy, setBusy] = useState<"learn" | "apply" | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenantIntegrationConfig(tenantId, slug)), (snap) => {
      const l = (snap.get("learn") as QbLearn | undefined) ?? null;
      setLearn(l);
      setActive((snap.get("syncProfile") as QbSyncProfile | undefined) ?? null);
      // Each fresh proposal resets the editable copy.
      setDraft(l?.status === "ready" && l.proposed ? l.proposed : null);
    });
  }, [tenantId, slug]);

  async function startLearn() {
    if (!tenantId) return;
    setBusy("learn");
    setNote("");
    try {
      const res = await call<{ outcome?: string; sampledInvoices?: number }>(
        target === "online" ? "learnQbSyncProfile" : "learnQbdSyncProfile",
        { tenantId },
      );
      if (res.outcome === "already_running") setNote("Already learning — results show here when QuickBooks answers.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function apply() {
    if (!tenantId || !draft) return;
    setBusy("apply");
    setNote("");
    try {
      await call("applyQbSyncProfile", { tenantId, target, profile: draft });
      setNote("Applied. Invoices you sync from now on use these settings; already-synced invoices are unchanged.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const status = learn?.status;
  const reasons = learn?.reasons ?? {};
  return (
    <div className="mt-4 border-t border-steel/15 pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-ink">Match how you already invoice</p>
        <Button
          variant="ghost"
          className="px-2 py-1 text-xs"
          busy={busy === "learn"}
          disabled={status === "waiting_for_quickbooks" || status === "analyzing"}
          onClick={() => void startLearn()}
        >
          <Sparkles size={13} /> {learn ? "Learn again" : "Learn from my QuickBooks"}
        </Button>
      </div>
      <p className="mt-0.5 text-xs text-steel">
        Clevis reads your recent QuickBooks invoices and proposes the items, class, tax code, terms and PO placement
        that make Clevis invoices look like yours. You review it first; nothing changes until you apply.
      </p>

      {active ? <p className="mt-1 text-[11px] text-ink">Active now: {describe(active).join(" · ")}</p> : null}

      {status === "waiting_for_quickbooks" ? (
        <p className="mt-2 rounded-lg bg-steel/10 p-2 text-xs text-ink">
          Waiting for QuickBooks Desktop. The Web Connector picks this up on its next check-in (about a minute) — keep
          QuickBooks open with your company file.
        </p>
      ) : null}
      {status === "analyzing" ? (
        <p className="mt-2 rounded-lg bg-steel/10 p-2 text-xs text-ink">Reading your invoices and working out your setup…</p>
      ) : null}
      {status === "error" ? (
        <p className="mt-2 rounded-lg bg-brandorange/10 p-2 text-xs text-ink">{learn?.error ?? "Learning didn't finish."} Click Learn again to retry.</p>
      ) : null}

      {status === "ready" && draft ? (
        <div className="mt-2 space-y-1.5 rounded-lg border border-steel/20 p-2 text-xs">
          <p className="text-ink">
            <strong>Suggested from your last {learn?.sampledInvoices} invoices.</strong> {learn?.summary}
          </p>
          <Row label="Default item" value={draft.defaultItemName ?? "“Clevis Services”"} why={reasons.defaultItem} />
          {draft.itemRules.length > 0 ? (
            <div>
              <span className="text-steel">Keyword rules</span>
              <ul className="ml-3 mt-0.5 space-y-0.5">
                {draft.itemRules.map((r) => (
                  <li key={r.contains} className="flex items-center gap-1.5 text-ink">
                    lines mentioning “{r.contains}” → {r.itemName}
                    <button
                      type="button"
                      aria-label={`Remove rule ${r.contains}`}
                      className="text-steel hover:text-danger"
                      onClick={() => {
                        const itemRules = draft.itemRules.filter((x) => x.contains !== r.contains);
                        setDraft({ ...draft, itemRules, itemStrategy: itemRules.length > 0 ? "rules" : "generic" });
                      }}
                    >
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
              {reasons.itemRules ? <p className="ml-3 text-[11px] text-steel">{reasons.itemRules}</p> : null}
            </div>
          ) : null}
          <Row label="Class" value={draft.className ?? "none"} why={reasons.class} />
          <Row label="Tax code" value={draft.taxCodeName ?? "QuickBooks default"} why={reasons.taxCode} />
          <Row label="Terms" value={draft.termsName ?? "QuickBooks default"} why={reasons.terms} />
          <Row
            label="Builder PO"
            value={`${PO_LABEL[draft.poPlacement]}${draft.poPlacement === "customField" && draft.poCustomFieldName ? ` “${draft.poCustomFieldName}”` : ""}`}
            why={reasons.poPlacement}
          />
          <div className="flex gap-1.5 pt-1">
            <Button className="px-3 py-1 text-xs" busy={busy === "apply"} onClick={() => void apply()}>
              Apply these settings
            </Button>
          </div>
        </div>
      ) : null}
      {note ? <p className="mt-1 text-xs text-ink">{note}</p> : null}
    </div>
  );
}

function Row({ label, value, why }: { label: string; value: string; why?: string }) {
  return (
    <div>
      <span className="text-steel">{label}:</span> <span className="text-ink">{value}</span>
      {why ? <p className="ml-3 text-[11px] text-steel">{why}</p> : null}
    </div>
  );
}
